import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const REASONS = [
  { v: "wrong_item", l: "Wrong item received" },
  { v: "counterfeit", l: "Item is counterfeit / fake" },
  { v: "size_mismatch", l: "Size doesn't match listing" },
  { v: "damaged", l: "Arrived damaged" },
  { v: "not_received", l: "Never received" },
  { v: "other", l: "Other issue" },
];

export default function ReportIssueModal({ order, onClose, onSubmitted }) {
  const [reason, setReason] = useState("wrong_item");
  const [description, setDescription] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 15) return toast.error("Describe the issue (min 15 chars)");
    setSaving(true);
    try {
      await api.post("/disputes", { order_id: order.order_id, reason, description, evidence_urls: evidence });
      toast.success("Report filed — we'll investigate within 48h");
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to file report");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="report-issue-modal">
      <div className="bg-white border-2 border-black brutal-shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-[#FF007F] text-white border-b-2 border-black p-4 flex items-center justify-between">
          <h3 className="font-display text-2xl">REPORT AN ISSUE</h3>
          <button onClick={onClose} data-testid="report-close" className="border-2 border-black bg-white text-black p-1"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="font-mono-b text-xs text-black/70">Order <b>{order.order_id?.slice(0, 16)}</b> — total ₹{order.total}</p>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">REASON</label>
            <select
              data-testid="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="brutal-input w-full mt-1 uppercase"
            >
              {REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">DESCRIPTION</label>
            <textarea
              data-testid="report-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="brutal-input w-full mt-1"
              placeholder="Explain what happened. Include photos/video below."
              required
            />
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase block mb-2">PHOTO / VIDEO EVIDENCE</label>
            <ImageUpload value={evidence} onChange={setEvidence} max={4} testIdPrefix="report-evidence" />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="report-submit"
            className="bg-[#FF007F] text-white border-2 border-black brutal-shadow hover-nudge px-6 py-3 font-mono-b font-bold uppercase disabled:opacity-50 w-full"
          >
            {saving ? "SUBMITTING..." : "FILE REPORT"}
          </button>
        </form>
      </div>
    </div>
  );
}
